import * as Sentry from '@sentry/react'
import {
  resolveSentryEnvironment,
  scrubSentryEvent,
} from '@order-up/analytics/sentry'

/**
 * Sentry browser bootstrap, shared by BOTH HTML entries — the customer app
 * (main.tsx) and the POS keyed-entry page (embedded.tsx). The keyed-entry page
 * used to have none, so a failure there had one witness: a console inside a
 * WebView. `surface` tells the two apart in one Sentry project.
 *
 * Resolved once and shared by `environment` and `beforeSend` — they disagreed
 * before (one read VITE_APP_ENV first, the other VITE_VERCEL_ENV), so an event
 * could be labelled `production` by one and judged non-production by the other.
 */
export function initCheckoutSentry(surface: 'customer-checkout' | 'pos-keyed-entry'): void {
  const sentryEnvironment = resolveSentryEnvironment(
    import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined,
    import.meta.env.VITE_APP_ENV as string | undefined,
    import.meta.env.VITE_VERCEL_ENV as string | undefined,
    import.meta.env.MODE,
  )

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (dsn) {
    Sentry.init({
      dsn,
      environment: sentryEnvironment,
      release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
      sampleRate: parseFloat(
        (import.meta.env.VITE_SENTRY_SAMPLE_RATE as string | undefined) || '1.0',
      ),
      tracesSampleRate: parseFloat(
        (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined)
          || '0.1',
      ),
      initialScope: {
        tags: { app: 'checkout', surface },
      },
      sendDefaultPii: false,
      beforeSend: (event) => scrubSentryEvent(event, sentryEnvironment),
    })
  } else if (import.meta.env.PROD) {
    // Loud breadcrumb: a production build with no DSN means client-side errors
    // — including a white-screen crash like the 2026-05-30 duplicate-React
    // incident — are NOT captured. Set VITE_SENTRY_DSN in the checkout Vercel
    // project so the error monitoring + alerting actually has a feed.
    console.warn(
      '[checkout] VITE_SENTRY_DSN is not set — client error monitoring is DISABLED for this build.',
    )
  }
}
